/**
 * LA FICHE DE VIE D'UN APPAREIL — module PUR (29/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Dans la fiche de vie je veux voir les éléments clés de la vie de
 *  l'équipement = toutes les NC et maintenances et notamment la maintenance
 *  d'acquisition qui est un peu plus spéciale. » — Ines, 29/10/2026 ; retenus
 *  aussi : métrologie, changements d'état, indicateurs en tête.
 *
 * Ce module ne LIT rien : il ASSEMBLE ce que l'écran a déjà reçu de cinq
 * sources qui existent — le dossier de mise en service, les NC, l'historique
 * des maintenances (`/ged/maintenances/historique`), le registre des
 * calibrations, et le journal de l'appareil — en une frise unique. Il est pur
 * pour être testé sans écran (`front-vie-equipement`).
 *
 * ── Trois partis pris ───────────────────────────────────────────────────────
 *
 *  · L'ACQUISITION n'est pas un événement parmi d'autres : c'est la naissance
 *    de l'appareil (réception, installation, qualification, autorisation
 *    d'usage). Elle est rendue À PART, en tête, depuis le dossier de mise en
 *    service — et ses étapes ne sont pas recopiées dans la frise.
 *  · Les CALIBRATIONS sont des centaines (679 sur l'EQ09) : elles sont
 *    regroupées par MOIS, et seules celles qui ont échoué sont détaillées.
 *    Une frise noyée sous la routine ne montre plus rien de la vie.
 *  · Rien n'est compté deux fois : l'acquittement d'une maintenance figure au
 *    journal ET à l'historique — la frise ne le prend qu'à l'historique.
 */
import type { GedEquipement, GedEvent, GedNC } from './types';
import type { EntreeHistorique } from './historique-maintenance';

export type Famille = 'nc' | 'maintenance' | 'metrologie' | 'etat';

export const FAMILLES: { k: Famille; label: string }[] = [
  { k: 'nc', label: 'Non-conformités' },
  { k: 'maintenance', label: 'Maintenances' },
  { k: 'metrologie', label: 'Métrologie' },
  { k: 'etat', label: 'Changements d’état' },
];

export interface Evenement {
  cle: string;
  /** AAAA-MM-JJ, ou '' quand la source n'en porte pas (rangé en fin de frise). */
  date: string;
  famille: Famille;
  /** Libellé court de la pastille : « Panne », « Préventive », « Étalonnage »… */
  nature: string;
  titre: string;
  detail: string;
  /** Un écueil : NC ouverte, maintenance non conforme, calibration non valide. */
  alerte: boolean;
  /** Ce qu'on ouvre au clic. */
  /** `maintenance` porte l'identifiant de l'intervention (fiche de vie, acte du
   *  plan, vérification) : le clic ouvre SON détail dans l'onglet Maintenance
   *  (spec 002, récit 1) ; sans `id`, la ligne regroupe une routine. */
  lien?: { type: 'nc'; documentId: string } | { type: 'maintenance'; id?: string } | { type: 'calibrations' };
}

export interface EtapeAcquisition { libelle: string; fait: boolean; faitLe: string; sansObjet: boolean; bloquante: boolean }
export interface Acquisition {
  dateReception: string;
  dateMiseEnService: string;
  fournisseur: string;
  garantieJusquA: string;
  /** Le dossier de mise en service : absent sur les appareils repris du classeur. */
  dossier: null | {
    ouvertLe: string;
    autoriseLe: string;
    autorisePar: string;
    etapes: EtapeAcquisition[];
    faites: number;
    retenues: number;
  };
}

export interface Indicateurs {
  /** Âge depuis la mise en service (à défaut la réception), en mois ; null si inconnu. */
  ageMois: number | null;
  pannesTotal: number;
  pannes12m: number;
  ncOuvertes: number;
  /** Arrêt cumulé, en jours, des durées LISIBLES ; `arretsIllisibles` compte les autres. */
  joursArret: number;
  arretsIllisibles: number;
  derniereMaintenance: string;
  /** LA prochaine échéance de l'appareil, lue dans son état de maintenance
   *  (spec 002, R5 : plan ou métrologie, la plus proche) — jamais recalculée ici. */
  prochaineEcheance: string;
  /** « maintenance » | « metrologie » | '' : de quoi parle la prochaine échéance. */
  prochaineNature: string;
  /** Échéances en retard (plan et métrologie), lues dans l'état de maintenance. */
  echeancesEnRetard: number;
  finGarantie: string;
  finContrat: string;
}

export interface Calibration {
  ref?: string; date: string; analyteLibelle?: string; resultat?: string;
  typeCalibration?: string; ncRef?: string | null; annule?: boolean;
}

const txt = (v: unknown) => String(v ?? '').trim();
const jour = (v: unknown) => txt(v).slice(0, 10);
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** « 2 h », « 3 jours », « 1,5 j », « 48h » → jours ; null si on ne sait pas lire. */
export function joursDArret(v: unknown): number | null {
  const s = txt(v).toLowerCase().replace(',', '.');
  if (!s) return null;
  const m = /^(\d+(?:\.\d+)?)\s*(h|heures?|j|jours?|sem(?:aines?)?|mois)\b/.exec(s);
  if (!m) return null;
  const n = Number(m[1]);
  const u = m[2];
  if (u.startsWith('h')) return n / 24;
  if (u.startsWith('sem')) return n * 7;
  if (u === 'mois') return n * 30;
  return n;
}

const STATUTS_NC_OUVERTS = new Set(['declaree', 'encours', 'soldee']);

/** L'identifiant d'une intervention dans l'adresse de l'onglet Maintenance —
 *  même règle que `idIntervention` (`maintenance-appareil.ts`), recopiée ici
 *  pour que ce module reste sans dépendance. */
function idInterventionVie(m: EntreeHistorique): string {
  if (m.origine === 'fiche' && m.ivId) return m.ivId;
  if (m.origine === 'plan' && m.acteId) return m.acteId;
  return m.reference || m.cle;
}
const NATURE_NC: Record<string, string> = {
  equipement: 'Panne', cqi: 'NC du CQI', eeq: 'NC d’EEQ', reclamation: 'Réclamation',
};

/** Les segments du journal « Fiche modifiée » qui disent un CHANGEMENT D'ÉTAT, pas une correction de saisie. */
const SEGMENTS_ETAT: { re: RegExp; nature: string }[] = [
  { re: /^Logiciel — version installée/, nature: 'Version logicielle' },
  { re: /^Régime/, nature: 'Régime' },
  { re: /^Statut/, nature: 'Statut' },
  { re: /^Mise en réforme|^Date de réforme|^Réforme/, nature: 'Réforme' },
];

export function assemblerVie(src: {
  equipement: Pick<GedEquipement, 'code' | 'dateReception' | 'dateMiseEnService' | 'fournisseur' | 'garantieJusquA' | 'contrat' | 'miseEnService' | 'metrologie'>;
  ncs: GedNC[];
  maintenances: EntreeHistorique[];
  calibrations: Calibration[];
  journal: Pick<GedEvent, 'evType' | 'ts' | 'payload' | 'actorName'>[];
  /** L'état de maintenance de l'appareil, CALCULÉ PAR LE SERVEUR (spec 002,
   *  récit 7) : la Fiche de vie lit la même prochaine échéance que l'en-tête,
   *  la liste du parc, l'étiquette et les tâches. Avant, elle prenait la plus
   *  ANCIENNE échéance du plan non faite — une troisième date. */
  etatMaintenance?: { retards: number; prochaine: { date: string; nature: string } | null } | null;
  aujourdhui: string;
}): { acquisition: Acquisition; evenements: Evenement[]; indicateurs: Indicateurs } {
  const e = src.equipement;
  const ev: Evenement[] = [];

  /* ── Acquisition ─────────────────────────────────────────────────────── */
  const d = e.miseEnService;
  const etapes = (d?.etapes || []).slice().sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)).map((x) => ({
    libelle: txt(x.libelle), fait: !!x.fait, faitLe: jour(x.faitLe), sansObjet: !!x.sansObjet, bloquante: !!x.bloquante,
  }));
  const retenues = etapes.filter((x) => !x.sansObjet);
  const acquisition: Acquisition = {
    dateReception: txt(e.dateReception), dateMiseEnService: txt(e.dateMiseEnService),
    fournisseur: txt(e.fournisseur), garantieJusquA: txt(e.garantieJusquA),
    dossier: d ? {
      ouvertLe: jour(d.ouvertLe), autoriseLe: jour(d.autoriseLe), autorisePar: txt(d.autorisePar),
      etapes, faites: retenues.filter((x) => x.fait).length, retenues: retenues.length,
    } : null,
  };

  /* ── Non-conformités — toutes, pas seulement les pannes ─────────────── */
  for (const n of src.ncs) {
    const ouverte = STATUTS_NC_OUVERTS.has(txt(n.statut));
    ev.push({
      cle: `nc-${n.documentId}`, date: jour(n.dateDetection), famille: 'nc',
      nature: NATURE_NC[txt(n.source)] || 'Non-conformité',
      titre: `${txt(n.ref)} — ${txt(n.titre)}`,
      detail: [txt(n.gravite) && `gravité ${txt(n.gravite)}`, n.dureeArret && `arrêt ${txt(n.dureeArret)}`,
        ouverte ? 'en cours' : (n.dateCloture ? `close le ${jour(n.dateCloture)}` : txt(n.statut))].filter(Boolean).join(' · '),
      alerte: ouverte, lien: { type: 'nc', documentId: n.documentId },
    });
  }

  /* ── Maintenances (et ce qui, dans l'historique, est de la métrologie) ─ */
  /* ⚠️ LA ROUTINE SE REGROUPE PAR MOIS (31/10/2026). La maintenance journalière
     d'un automate, c'est vingt lignes par mois — 82 sur l'EQ09 au test du
     31/10 — et la frise n'en montrait plus rien d'autre. Une préventive du PLAN
     sans écueil se compte par mois et par intitulé ; tout le reste (curative,
     hors plan, non conforme, avec NC ou arrêt) reste détaillé. */
  const routine = new Map<string, { mois: string; intitule: string; n: number }>();
  const estRoutine = (m: EntreeHistorique) => m.origine === 'plan' && m.type === 'preventive'
    && !m.probleme && m.conforme !== false && !txt(m.ncRef) && !txt(m.dureeArret)
    && !['etalonnage', 'verification'].includes(txt(m.intervType)) && ISO.test(jour(m.date));
  for (const m of src.maintenances.filter(estRoutine)) {
    const mois = jour(m.date).slice(0, 7);
    const intitule = txt(m.intitule) || 'Maintenance préventive';
    const k = `${mois}|${intitule}`;
    const g = routine.get(k) || { mois, intitule, n: 0 };
    g.n += 1; routine.set(k, g);
  }
  for (const [k, g] of routine) {
    ev.push({
      cle: `mt-routine-${k}`, date: `${g.mois}-01`, famille: 'maintenance', nature: 'Préventive',
      titre: `${g.intitule} — ${g.n} fois en ${moisLisible(g.mois)}`,
      detail: 'maintenance du plan, faite sans écueil', alerte: false, lien: { type: 'maintenance' },
    });
  }
  for (const m of src.maintenances.filter((x) => !estRoutine(x))) {
    const metro = ['etalonnage', 'verification'].includes(txt(m.intervType));
    const nature = metro ? (m.intervType === 'etalonnage' ? 'Étalonnage' : 'Vérification')
      : m.type === 'preventive' ? 'Préventive' : m.type === 'curative' ? 'Curative' : 'Maintenance';
    ev.push({
      cle: `mt-${m.cle}`, date: jour(m.date), famille: metro ? 'metrologie' : 'maintenance', nature,
      titre: txt(m.intitule) || nature,
      detail: [txt(m.intervenant) || txt(m.prestataire), m.dureeArret && `arrêt ${txt(m.dureeArret)}`,
        m.conforme === false ? 'NON CONFORME' : '', m.ncRef && `NC ${txt(m.ncRef)}`, txt(m.commentaire)].filter(Boolean).join(' · '),
      alerte: m.probleme || m.conforme === false, lien: { type: 'maintenance', id: idInterventionVie(m) },
    });
  }

  /* ── Métrologie : certificat, vérification intermédiaire, calibrations ─ */
  const mt = e.metrologie;
  if (mt?.dateCertificat) {
    ev.push({
      cle: 'metro-certificat', date: jour(mt.dateCertificat), famille: 'metrologie', nature: 'Certificat',
      titre: `Certificat${mt.refCertificat ? ` n° ${txt(mt.refCertificat)}` : ''}${mt.organisme ? ` — ${txt(mt.organisme)}` : ''}`,
      detail: [mt.constat && `constat : ${txt(mt.constat)}`, mt.dateValiditeCertificat && `valable jusqu’au ${jour(mt.dateValiditeCertificat)}`,
        mt.certificatPieceId ? 'pièce jointe' : 'pièce non jointe'].filter(Boolean).join(' · '),
      alerte: !!mt.dateValiditeCertificat && jour(mt.dateValiditeCertificat) < src.aujourdhui,
    });
  }
  if (mt?.derniereVerifInterm) {
    ev.push({
      cle: 'metro-verif', date: jour(mt.derniereVerifInterm), famille: 'metrologie', nature: 'Vérification intermédiaire',
      titre: 'Vérification intermédiaire', detail: mt.verifIntermPeriodiciteMois ? `tous les ${txt(mt.verifIntermPeriodiciteMois)} mois` : '',
      alerte: false,
    });
  }
  const calibs = src.calibrations.filter((c) => !c.annule && ISO.test(jour(c.date)));
  const parMois = new Map<string, { n: number; echecs: number }>();
  for (const c of calibs) {
    const mois = jour(c.date).slice(0, 7);
    const g = parMois.get(mois) || { n: 0, echecs: 0 };
    g.n += 1;
    if (c.resultat === 'non_valide') {
      g.echecs += 1;
      ev.push({
        cle: `cal-${c.ref || `${c.date}-${c.analyteLibelle}`}`, date: jour(c.date), famille: 'metrologie', nature: 'Calibration non valide',
        titre: `Calibration non valide — ${txt(c.analyteLibelle)}`,
        detail: [txt(c.typeCalibration), c.ncRef && `NC ${txt(c.ncRef)}`].filter(Boolean).join(' · '),
        alerte: true, lien: { type: 'calibrations' },
      });
    }
    parMois.set(mois, g);
  }
  for (const [mois, g] of parMois) {
    ev.push({
      cle: `cal-mois-${mois}`, date: `${mois}-01`, famille: 'metrologie', nature: 'Calibrations',
      titre: `${g.n} calibration${g.n > 1 ? 's' : ''} en ${moisLisible(mois)}`,
      detail: g.echecs ? `dont ${g.echecs} non valide${g.echecs > 1 ? 's' : ''}` : 'toutes valides',
      alerte: false, lien: { type: 'calibrations' },
    });
  }

  /* ── Changements d'état, lus au journal ─────────────────────────────── */
  src.journal.forEach((j, i) => {
    const date = jour(j.ts);
    const p = txt(j.payload);
    const par = txt(j.actorName);
    const pousser = (nature: string, titre: string) => ev.push({
      cle: `j-${i}-${nature}`, date, famille: 'etat', nature, titre, detail: par ? `par ${par}` : '', alerte: false,
    });
    if (j.evType === 'EquipementCree') pousser('Entrée au parc', p);
    else if (j.evType === 'EquipementStatut') pousser('Statut', p);
    else if (j.evType === 'EmplacementPose') pousser('Emplacement', p);
    else if (j.evType === 'FicheEquipementModifiee' || j.evType === 'equipement.fiche') {
      for (const seg of p.split(' · ')) {
        const s = SEGMENTS_ETAT.find((x) => x.re.test(seg.trim()));
        if (s) pousser(s.nature, seg.trim());
      }
    }
  });

  /* Du plus récent au plus ancien ; les sans-date en dernier, jamais perdus. */
  ev.sort((a, b) => (!a.date && !b.date ? 0 : !a.date ? 1 : !b.date ? -1 : b.date.localeCompare(a.date)));

  /* ── Indicateurs ─────────────────────────────────────────────────────── */
  const debut = ISO.test(jour(e.dateMiseEnService)) ? jour(e.dateMiseEnService)
    : ISO.test(jour(e.dateReception)) ? jour(e.dateReception) : '';
  const ageMois = debut ? Math.max(0, moisEntre(debut, src.aujourdhui)) : null;
  const pannes = src.ncs.filter((n) => n.source === 'equipement');
  const ilYAUnAn = decalerAns(src.aujourdhui, -1);
  let joursArret = 0; let arretsIllisibles = 0;
  for (const v of [...src.ncs.map((n) => n.dureeArret), ...src.maintenances.map((m) => m.dureeArret)]) {
    if (!txt(v)) continue;
    const j = joursDArret(v);
    if (j === null) arretsIllisibles += 1; else joursArret += j;
  }
  const faites = src.maintenances.map((m) => jour(m.date)).filter((x) => ISO.test(x)).sort();
  const indicateurs: Indicateurs = {
    ageMois,
    pannesTotal: pannes.length,
    pannes12m: pannes.filter((n) => jour(n.dateDetection) >= ilYAUnAn).length,
    ncOuvertes: src.ncs.filter((n) => STATUTS_NC_OUVERTS.has(txt(n.statut))).length,
    joursArret: Math.round(joursArret * 10) / 10,
    arretsIllisibles,
    derniereMaintenance: faites[faites.length - 1] || '',
    prochaineEcheance: jour(src.etatMaintenance?.prochaine?.date),
    prochaineNature: txt(src.etatMaintenance?.prochaine?.nature),
    echeancesEnRetard: src.etatMaintenance?.retards ?? 0,
    finGarantie: txt(e.garantieJusquA),
    finContrat: jour(e.contrat?.fin),
  };
  return { acquisition, evenements: ev, indicateurs };
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export function moisLisible(aaaaMm: string): string {
  const [a, m] = aaaaMm.split('-');
  return `${MOIS[Number(m) - 1] || m} ${a}`;
}
function moisEntre(du: string, au: string): number {
  const [a1, m1, j1] = du.split('-').map(Number);
  const [a2, m2, j2] = au.split('-').map(Number);
  return (a2 - a1) * 12 + (m2 - m1) - (j2 < j1 ? 1 : 0);
}
function decalerAns(iso: string, n: number): string {
  return `${Number(iso.slice(0, 4)) + n}${iso.slice(4, 10)}`;
}
/** « 3 ans et 2 mois », « 7 mois ». */
export function ageLisible(mois: number): string {
  const a = Math.floor(mois / 12); const m = mois % 12;
  if (!a) return `${m} mois`;
  return `${a} an${a > 1 ? 's' : ''}${m ? ` et ${m} mois` : ''}`;
}

/** Filtre de la frise : familles retenues, année, recherche plein texte. */
export function filtrerVie(ev: Evenement[], f: { familles: Famille[]; annee: string; q: string }): Evenement[] {
  const q = f.q.trim().toLowerCase();
  return ev.filter((x) => (!f.familles.length || f.familles.includes(x.famille))
    && (!f.annee || x.date.startsWith(f.annee))
    && (!q || `${x.nature} ${x.titre} ${x.detail}`.toLowerCase().includes(q)));
}
